import {Transform, Type} from 'class-transformer';
import {User, UserImpl} from '@app/layout/admin/services/api/user-interfaces';
import moment, {Moment, Duration} from 'moment-timezone';
import {CollectionUtils, IterableUtil} from '@shared/util/collections/collection-utils';
import {Equipment, EquipmentId, LevelId, LocalDateTimeRangeTuple, Metadata, MetadataId, MetadataType, MetadataTypeId, Scalars, ZoneNameId} from '@app/graphql-typings/graphql';
import {SiteId, SiteInfo} from '@shared/services/api/access-sites.interface';
import {APIResponse} from '@app/layout/admin/services/api/api.interface';
import {AttachmentSharedBase} from '@app/shared/components/attachments/attachments.interface';
import {ImpactId, PriorityId} from '@app/shared/api/model/type-ids';
import {CacheById} from '@app/shared/data-stores/cache-by-id';
import {Priority, Template} from "@app/layout/rules/template-editor/api/template";
import {TemplateCategoryId} from "@app/layout/rules/template-categories/models/template-category";
import {AlertNotification, SiteNotificationId} from '../notification.service';
import {EnumUtils} from '@app/shared/util/general/enum-utils';

export type TicketId = string;

export enum TicketType {
  ESCALATED = 'escalated',
  ALERT = 'alert',
}


export enum TicketEmailContentType {
  TEXT_PLAIN = 'text/plain'
}


export enum TicketBenefitId {
  BENEFIT_ENERGY = 1,
  BENEFIT_COMFORT = 2,
  BENEFIT_RELIABILITY = 3,
  BENEFIT_OTHER = 4,
  BENEFIT_SAFETY = 5,
  BENEFIT_WATER = 6,
}

export enum TicketImpactId {
  IMPACT_ENERGY = '4e8da0a2-f0d7-4d26-89cc-42bdb48a3aa3',
  IMPACT_COMFORT = 'a3554cff-e909-4810-be85-5318f07461fc',
  IMPACT_RELIABILITY = 'c2e1a282-0eb0-4ec9-b25b-924d7f7aebcd',
  IMPACT_OTHER = '3f8d4f56-f855-49c0-a1a8-b128197754fc',
  IMPACT_SAFETY = '813c161e-5971-489e-9b88-f3d0af656e5b',
  IMPACT_WATER = '49199edf-c534-49f1-8ec7-d35f47eb0040'
}

export namespace TicketImpactId {
  export function toBenefitId(impactId: TicketImpactId): TicketBenefitId | null {
    const impactKey = EnumUtils.getEnumKeyByValue(TicketImpactId, impactId);
    const benefitKey = impactKey?.replace('IMPACT', 'BENEFIT');
    return TicketBenefitId[benefitKey] ?? null;
  }

  export function parseAsBenefitId(rawImpactId: string): TicketBenefitId | null {
    const impactId = EnumUtils.parseEnum(TicketImpactId, rawImpactId) as TicketImpactId | null;
    return impactId != null ? toBenefitId(impactId) : null;
  }
}

export enum OldTicketPriorityId {
  PRIORITY_URGENT = 4,
  PRIORITY_MAINTENANCE = 5,
  PRIORITY_QUOTE = 6,
  PRIORITY_MONITOR = 7
}

export enum TicketPriorityId {
  PRIORITY_P1_CRITICAL = 1,
  PRIORITY_P2_URGENT = 2,
  PRIORITY_P3_ROUTINE = 3,
  PRIORITY_P4_PLANNED = 4,
  PRIORITY_P5_DEFERRED = 5,
  PRIORITY_NONE = 2146483647
}
export namespace TicketPriorityId {
  export function isHighPriority(priorityId: TicketPriorityId): boolean {
    return priorityId === TicketPriorityId.PRIORITY_P1_CRITICAL || priorityId === TicketPriorityId.PRIORITY_P2_URGENT;
  }
}

export enum TicketStatusId {
  STATUS_NEW = 1,
  STATUS_IN_PROGRESS = 3,
  STATUS_RESOLVED = 6,
  STATUS_ON_HOLD = 7,
  STATUS_NOT_DOING = 8,
}

export enum TicketAggregation {
  SITE_ID = 'site_id',
  STATUS_ID = 'status_id',
  ASSIGNEE_ID = 'assignee_id',
  HAS_LINKS = 'has_links',
  FAULT_STATUS = 'fault_status',
  TIME_IN_FAULT = 'time_in_fault',
  AGE = 'age',
  PRIORITY_ID = 'priority_id',
  BENEFIT_ID = 'benefit_id',
  IMPACT_ID = 'impact_id',
}


export enum TicketFaultStatus {
  FAULT = 'fault',
  RECOVERED = 'recovered',
}

export const allFaultStatuses = Object.values(TicketFaultStatus);

export const ticketStatusIds = new Set<TicketStatusId>([
  TicketStatusId.STATUS_NEW,
  TicketStatusId.STATUS_IN_PROGRESS,
  TicketStatusId.STATUS_ON_HOLD,
  TicketStatusId.STATUS_NOT_DOING,
  TicketStatusId.STATUS_RESOLVED
]);

export const resolvedTicketStatusIds = new Set<TicketStatusId>([
  TicketStatusId.STATUS_NOT_DOING,
  TicketStatusId.STATUS_RESOLVED,
]);

export const unresolvedTicketStatusIds = new Set<TicketStatusId>(
    [...ticketStatusIds].filter(statusId => !resolvedTicketStatusIds.has(statusId)));

export const ticketBenefits: TicketBenefit[] = [
  {
    benefit_id: TicketBenefitId.BENEFIT_COMFORT,
    benefit_name: 'Comfort'
  },
  {
    benefit_id: TicketBenefitId.BENEFIT_ENERGY,
    benefit_name: 'Energy'
  },
  {
    benefit_id: TicketBenefitId.BENEFIT_OTHER,
    benefit_name: 'Other'
  },
  {
    benefit_id: TicketBenefitId.BENEFIT_RELIABILITY,
    benefit_name: 'Reliability'
  },
  {
    benefit_id: TicketBenefitId.BENEFIT_SAFETY,
    benefit_name: 'Safety'
  },
  {
    benefit_id: TicketBenefitId.BENEFIT_WATER,
    benefit_name: 'Water'
  }
];
export const ticketBenefitsById: Map<TicketBenefitId, TicketBenefit> = CollectionUtils.indexBy(ticketBenefits, 'benefit_id');

export const ticketBenefitByImpactId: Map<string, TicketBenefit> = new Map([
  [TicketImpactId.IMPACT_ENERGY, ticketBenefitsById.get(TicketBenefitId.BENEFIT_ENERGY)],
  [TicketImpactId.IMPACT_COMFORT, ticketBenefitsById.get(TicketBenefitId.BENEFIT_COMFORT)],
  [TicketImpactId.IMPACT_RELIABILITY, ticketBenefitsById.get(TicketBenefitId.BENEFIT_RELIABILITY)],
  [TicketImpactId.IMPACT_OTHER, ticketBenefitsById.get(TicketBenefitId.BENEFIT_OTHER)],
  [TicketImpactId.IMPACT_SAFETY, ticketBenefitsById.get(TicketBenefitId.BENEFIT_SAFETY)],
  [TicketImpactId.IMPACT_WATER, ticketBenefitsById.get(TicketBenefitId.BENEFIT_WATER)],
]);

export const OldActionPriorities: TicketPriority[] = [
  {
    priority_id: OldTicketPriorityId.PRIORITY_MAINTENANCE,
    name: 'Maintenance',
  },
  {
    priority_id: OldTicketPriorityId.PRIORITY_MONITOR,
    name: 'Monitor',
  },
  {
    priority_id: OldTicketPriorityId.PRIORITY_QUOTE,
    name: 'Quote',
  },
  {
    priority_id: OldTicketPriorityId.PRIORITY_URGENT,
    name: 'Urgent',
  }
];


export const oldActionPrioritiesById: Map<OldTicketPriorityId, TicketPriority> = CollectionUtils.indexBy(OldActionPriorities, 'priority_id');


const ticketStatusNew: TicketStatus = {
  status_id: TicketStatusId.STATUS_NEW,
  status_name: 'New',
  display_text: 'New'
};


const ticketStatusActive: TicketStatus = {
  status_id: TicketStatusId.STATUS_NEW,
  status_name: 'New',
  display_text: 'Active'
};


const ticketStatusInProgress: TicketStatus = {
  status_id: TicketStatusId.STATUS_IN_PROGRESS,
  status_name: 'In Progress',
  display_text: 'In Progress'
};


const ticketStatusOnHold: TicketStatus = {
  status_id: TicketStatusId.STATUS_ON_HOLD,
  status_name: 'On Hold',
  display_text: 'On Hold'
};


const ticketStatusClosed = {
  status_id: TicketStatusId.STATUS_RESOLVED,
  status_name: 'Resolved',
  display_text: 'Closed'
};


const ticketStatusNotDoing = {
  status_id: TicketStatusId.STATUS_NOT_DOING,
  status_name: 'Not Doing',
  display_text: 'Not Doing'
};


const ticketStatusIgnored = {
  status_id: TicketStatusId.STATUS_NOT_DOING,
  status_name: 'Not Doing',
  display_text: 'Ignored'
};


export const unResolvedActionStatuses: TicketStatus[] = [
  ticketStatusNew,
  ticketStatusInProgress,
  ticketStatusOnHold,
];


export const resolvedActionStatuses: TicketStatus[] = [
  ticketStatusClosed,
  ticketStatusNotDoing,
];


export const actionStatuses: TicketStatus[] = [
    ...unResolvedActionStatuses,
    ...resolvedActionStatuses,
];

const alertStatuses: TicketStatus[] = [
    ticketStatusActive,
    ticketStatusIgnored,
    ticketStatusClosed,
];

export const actionStatusesById: Map<TicketStatusId, TicketStatus> = CollectionUtils.indexBy(actionStatuses, 'status_id');
export const alertStatusesById: Map<TicketStatusId, TicketStatus> = CollectionUtils.indexBy(alertStatuses, 'status_id');

/** Alert status ids without ignored */
export const trackedAlertStatusIds: TicketStatusId[] = Array.from(IterableUtil.filter(alertStatusesById.keys(), status => status !== ticketStatusIgnored.status_id));

export const colorByActionStatus: Map<TicketStatusId, string> = new Map<TicketStatusId, string>([
  [TicketStatusId.STATUS_NEW, 'info'],
  [TicketStatusId.STATUS_IN_PROGRESS, 'primary'],
  [TicketStatusId.STATUS_RESOLVED, 'success'],
  [TicketStatusId.STATUS_ON_HOLD, 'warning'],
  [TicketStatusId.STATUS_NOT_DOING, 'danger'],
]);

export const colorByAlertStatus: Map<TicketStatusId | TicketFaultStatus, string> = new Map<TicketStatusId | TicketFaultStatus, string>([
  [TicketStatusId.STATUS_NEW, 'info'],
  [TicketStatusId.STATUS_NOT_DOING, 'secondary'],
  [TicketStatusId.STATUS_RESOLVED, 'success'],
  [TicketFaultStatus.FAULT, 'orange'],
  [TicketFaultStatus.RECOVERED, 'teal'],
]);

export const ticketStatusColourMapByTicketType: Map<TicketType, Map<TicketStatusId | TicketFaultStatus, string>> = new Map([
    [TicketType.ALERT, colorByAlertStatus],
    [TicketType.ESCALATED, colorByActionStatus]
]);

const TICKET_LINK_TYPE_NAME_ALERT_TO_ACTION = 'alert_to_action';
export const TICKET_LINK_TYPE_ALERT_TO_ACTION: TicketLinkType = { name: TICKET_LINK_TYPE_NAME_ALERT_TO_ACTION };

export class TicketCommentImpl implements TicketComment {
  comment_id: string;
  ticket_id: string;
  @Type(() => moment)
  @Transform(({value}) => value ? moment.utc(value) : value, { toClassOnly: true })
  @Transform(({value}) => value ? value.toJSON() : value, { toPlainOnly: true })
  created_at: moment.Moment;
  text: string;
  author_id?: string;
  @Type(() => UserImpl)
  author?: User;
  author_full_name?: string;
}

export class AttachmentExpirableLinkImpl implements AttachmentExpirableLink {
  @Type(() => moment)
  @Transform(({value}) => value ? moment(value) : value, { toClassOnly: true })
  @Transform(({value}) => value ? value.format() : value, { toPlainOnly: true })
  expiration_ts: moment.Moment;
  @Type(() => URL)
  @Transform(({value}) => value ? new URL(value) : value, { toClassOnly: true })
  @Transform(({value}) => value ? value.toString() : value, { toPlainOnly: true })
  url: URL;
}

export class TicketAttachmentPreviewImpl implements TicketAttachmentPreview {
  height: number;
  width: number;
  @Type(() => AttachmentExpirableLinkImpl)
  link: AttachmentExpirableLink;
}

export class TicketAttachmentImpl implements TicketAttachment {
  attachment_id: string;
  ticket_id: string;
  author_id: string;
  file_name: string;
  @Type(() => AttachmentExpirableLinkImpl)
  link: AttachmentExpirableLink;
  mime_type: string;
  @Type(() => TicketAttachmentPreviewImpl)
  preview: TicketAttachmentPreview;
}


export interface TicketStatus {
  status_id: Scalars['Int'];
  status_name: Scalars['String'];
  display_text: Scalars['String'];
}

export interface TicketCommentCreationFields {
  ticket_id: Scalars['String'];
  text: Scalars['String'];
  anonymous_author_name?: Scalars['String'];
}

export type TicketCommentId = Scalars['String'];

export interface TicketCommentEditFields extends Omit<TicketCommentCreationFields, 'anonymous_author_name'> {
  comment_id: TicketCommentId;
}

export interface TicketComment extends TicketCommentEditFields {
  created_at: Scalars['DateTime'];
  author_full_name?: Scalars['String'];
  author_id?: Scalars['String'];
  author?: User;
}

export interface TicketSite {
  site_id: Scalars['Int'];
  site_name: Scalars['String'];
}

export interface TicketPriority {
  priority_id: Scalars['Int'];
  name: Scalars['String'];
}

export interface TicketBenefit {
  benefit_id: Scalars['Int'];
  benefit_name: Scalars['String'];
}

export interface TicketLinkType {
  name: string;
}

export enum TicketLinkDirection {
  TO_TARGET = 'target',
  TO_SOURCE = 'source',
}

export interface TicketLink {
  ticket_link_type: TicketLinkType;
  direction: TicketLinkDirection;
  ticket: Ticket;
}

export interface BaseTicketCreateBody {
  inbox?: boolean;
  type: TicketType;
  site_id: number;
  summary: string;
  description: string;
  status_id: TicketStatusId;
  solution: string | null;
  equipment_ids: number[] | null;
  created_at?: Moment;
  resolved_at?: Moment;
}

interface AlertTicketCreatePart {
  rule_id: number;
  fault_status: TicketFaultStatus,
  last_fault_transitioned_at?: Moment;
  target_ticket_id?: string;
}

interface EscalatedTicketCreatePart {
  savings?: number;
  time_taken?: Duration,
  assignee_ids: string[] | null;
  priority_id: TicketPriorityId | null;
  benefit_id: TicketBenefitId | null;
  source_ticket_ids?: string[] | null;
}

export interface EscalatedTicketCreateBody extends BaseTicketCreateBody, EscalatedTicketCreatePart {}

interface BaseTicketUpdateBody extends BaseTicketCreateBody {
  ticket_id: string;
  created_at: Moment;
  ticket_archived: boolean;
}

export interface EscalatedTicketUpdateBody extends BaseTicketUpdateBody, EscalatedTicketCreatePart {}

// TicketBody has only field returned by REST calls
interface BaseTicketReadBody extends BaseTicketUpdateBody {
  inbox: boolean;
  metadata_code?: Scalars['String'];
  savings?: Scalars['Float'];
  manual_savings: Scalars['Boolean'];
  resolved_at: Moment;
  updated_at: Moment;
}

interface AlertTicketReadPart extends AlertTicketCreatePart {
  // make fields non-optional in read part
  inbox: boolean;
  last_fault_transitioned_at: Moment;
  time_in_fault?: Scalars['Long'];
  power_saving?: Scalars['Float'] | null;
}

interface EscalatedTicketReadPart extends EscalatedTicketCreatePart {
  // make inbox non-optional in read part
  inbox: boolean;
  age?: Scalars['Long'];
}


interface AlertTicketBody extends BaseTicketReadBody, AlertTicketReadPart {}
export interface EscalatedTicketBody extends BaseTicketReadBody, EscalatedTicketReadPart {}

export type TicketBody = AlertTicketBody | EscalatedTicketBody;


// Ticket has all TicketBody fields plus fields those are extended or from graphql select.
export interface BaseTicket extends BaseTicketReadBody {
  site?: TicketSite;
  status?: TicketStatus;
  change_history_count?: Scalars['Int'];
  change_history?: TicketChangeHistory[];
  priority?: TicketPriority;
}

interface AlertTicketPart extends AlertTicketReadPart {
  ticket_links?: TicketLink[];
  impact_ids: string[];
}


interface EscalatedTicketPart extends EscalatedTicketReadPart {
  benefit?: TicketBenefit[];
  assignees?: User[];
  comment_count?: Scalars['Int']; // Only available for Escalated Tickets
  comments?: TicketComment[];
  attachments?: TicketAttachment[];
  ticket_links?: TicketLink[];
  time_taken?: Duration;
}


export interface AlertTicket extends BaseTicket, AlertTicketPart {}
export interface EscalatedTicket extends BaseTicket, EscalatedTicketPart {}

export type Ticket = AlertTicket | EscalatedTicket;
export type PartialTicket = Partial<Ticket>;


export interface AttachmentUploadUrl {
  uuid: string;
  file_name: string;
  upload_url: string;
}

export interface AttachmentExpirableLink {
  expiration_ts: moment.Moment;
  url: URL;
}

interface TicketAttachmentPreview {
  width: Scalars['Int'];
  height: Scalars['Int'];
  link: AttachmentExpirableLink;
}


export interface TicketAttachment extends AttachmentSharedBase<AttachmentExpirableLink> {
  attachment_id: Scalars['String'];
  ticket_id: Scalars['String'];
}

export interface TicketEmail {
  to: Array<Scalars['String']>;
  cc?: Array<Scalars['String']>;
  bcc?: Array<Scalars['String']>;
  subject: Scalars['String'];
  body: Scalars['String'];
  ['content-type']: TicketEmailContentType;
}


class BaseTicketImpl implements BaseTicket {

  @Type(() => TicketChangeHistoryImpl)
  @Transform(({value: changeHistory, obj: ticket}: {value: TicketChangeHistory[], obj: Ticket}) =>
    changeHistory ? changeHistory.map(history => {
      history.ticket_id = ticket.ticket_id;
      return history;
    }) : [], { toClassOnly: true })
  change_history?: TicketChangeHistory[];

  change_history_count: number;

  @Type(() => moment)
  @Transform(({value}) => value ? moment.utc(value) : value, { toClassOnly: true })
  @Transform(({value}) => value ? value.toJSON() : value, { toPlainOnly: true })
  created_at: moment.Moment;

  @Type(() => moment)
  @Transform(({value}) => value ? moment.utc(value) : value, { toClassOnly: true })
  @Transform(({value}) => value ? value.toJSON() : value, { toPlainOnly: true })
  resolved_at: moment.Moment;

  @Type(() => moment)
  @Transform(({value}) => value ? moment.utc(value) : value, { toClassOnly: true })
  @Transform(({value}) => value ? value.toJSON() : value, { toPlainOnly: true })
  updated_at: moment.Moment;

  description: string;
  equipment_ids: number[] | null;
  inbox: boolean;
  metadata_code: Scalars["String"];
  priority: TicketPriority;
  savings: Scalars["Float"];
  manual_savings: Scalars['Boolean'];
  site: TicketSite;
  site_id: number;
  solution: string | null;
  status: TicketStatus;
  status_id: TicketStatusId;
  summary: string;
  ticket_archived: boolean;
  ticket_id: string;
  type: TicketType;

  @Type(() => TicketLinkImpl)
  ticket_links?: TicketLink[];
}


export class AlertTicketImpl extends BaseTicketImpl implements AlertTicket {
  fault_status: TicketFaultStatus;
  rule_id: number;
  target_ticket_id: string;
  impact_ids: string[];

  @Type(() => moment)
  @Transform(({ value }) => value ? moment.utc(value) : value, { toClassOnly: true })
  @Transform(({ value }) => value ? value.toJSON() : value, { toPlainOnly: true })
  last_fault_transitioned_at: moment.Moment;

  time_in_fault?: Scalars['Long'];
  power_saving: Scalars['Float'] | null;
}


export class EscalatedTicketImpl extends BaseTicketImpl implements EscalatedTicket {
  comment_count?: number;

  @Type(() => TicketCommentImpl)
  @Transform(({value: comments, obj: ticket}: {value: TicketComment[], obj: Ticket}) =>
      comments ? comments.map(comment => {
        comment.ticket_id = ticket.ticket_id;
        return comment;
      }) : [], { toClassOnly: true })
  comments?: TicketComment[];

  @Type(() => TicketAttachmentImpl)
  @Transform(({value: attachments, obj: ticket}: {value: TicketAttachment[], obj: Ticket}) =>
      attachments ? attachments.map(attachment => {
        attachment.ticket_id = ticket.ticket_id;
        return attachment;
      }) : [], { toClassOnly: true })
  attachments?: TicketAttachment[];

  benefit?: TicketBenefit[];
  benefit_id: TicketBenefitId;

  @Type(() => UserImpl)
  assignees?: User[];
  assignee_ids: string[];

  priority_id: TicketPriorityId | null;

  @Type(() => moment)
  @Transform(({value}: { value: string | null }) => value ? moment.duration(value) : value, {toClassOnly: true})
  @Transform(({value}: { value: moment.Duration | null }) => value ? value.toJSON() : value, {toPlainOnly: true})
  time_taken: Duration;
  age?: number;
}


export interface TicketSearchCommentsParams {
  comment_author_ids?: string[];
  has_comments?: boolean;
}

export interface BaseTicketSearchParams {
  inbox?: boolean;
  ticket_id?: string;
  ticket_ids?: string[];
  site_ids?: number[];
  status_ids?: TicketStatusId[];
  metadata_type_ids?: number[];
  equipment_ids?: number[],
  level_ids?: LevelId[];
  zone_name_ids?: ZoneNameId[];
  summary_web_search?: string;
  ticket_archived?: boolean;
  updated_at_start?: moment.Moment;
  updated_at_end?: moment.Moment;
  // created_at_local timestamps are LocalDateTime type in graphql
  // when using JSON.stringify(moment.Moment), which in turn invokes moment.Moment.toJSON() method to serialise,
  // the result string would have timestamp portion at the end, which would then break the backend de-serialisation of LocalDateTime
  // therefore here we send a local date time string directly in the request.
  created_at_local_start?: string;
  created_at_local_end?: string;
  resolved_at_local_start?: string;
  resolved_at_local_end?: string;
  has_savings?: boolean;
  has_ticket_links?: boolean;
  linked_ticket_recorded_at_local_end?: string;
  rule_ids?: number[];
  priority_ids?: PriorityId[];
  resolved?: boolean;
  assignee_ids?: string[];
  totals?: string[];
}

export interface AlertTicketSearchParams extends BaseTicketSearchParams {
  in_alert_local_ts_range?: LocalDateTimeRangeTuple;
  template_ids?: string[];
  template_category_ids?: TemplateCategoryId[];
  impact_ids?: ImpactId[];
  fault_statuses?: TicketFaultStatus[];
  rule_state?: RuleState;
  rule_states?: RuleState[];
  time_in_fault_gt?: number;
  time_in_fault_gt_eq?: number;
  time_in_fault_lt?: number;
  time_in_fault_lt_eq?: number;
}

export interface EscalatedTicketSearchParams extends BaseTicketSearchParams, TicketSearchCommentsParams {
  benefit_ids?: TicketBenefitId[];
  age_gt?: number;
  age_gt_eq?: number;
  age_lt?: number;
  age_lt_eq?: number;
  age_cutoff_local_ts?: string;
}

export interface TicketSearchParams extends AlertTicketSearchParams, EscalatedTicketSearchParams {}

export enum TicketFieldId {
  TICKET_ID = 'ticket_id',
  ASSIGNEES = 'assignees',
  PRIORITY_ID = 'priority_id',
  BENEFIT_IDS = 'benefit_ids',
  SUMMARY = 'summary',
  DESCRIPTION = 'description',
  SOLUTION = 'solution',
  EQUIPMENT_IDS = 'equipment_ids',
  STATUS_ID = 'status_id',
  INBOX = 'inbox',
  CREATED_AT = 'created_at',
  RESOLVED_AT = 'resolved_at',
  UPDATED_AT = 'updated_at',
  ARCHIVED = 'ticket_archived',
  ATTACHMENTS = 'attachments',
  METADATA_CODE = 'metadata_code',
  SOURCE_TICKETS = 'source_tickets',
  TARGET_TICKETS = 'target_tickets',
  FAULT_STATUS = 'fault_status',
  LAST_FAULT_TRANSITIONED_AT ='last_fault_transitioned_at',
  COST_SAVINGS = 'savings',
  MANUAL_SAVINGS = 'manual_savings',
  IMPACT_IDS = 'impact_ids',
  TIME_TAKEN = 'time_taken'
}

export interface TicketChangeHistoryField {
  field_id: TicketFieldId;
  new_value: JSON;
  old_value: JSON;
}

export class TicketChangeHistoryFieldImpl implements TicketChangeHistoryField {
  field_id: TicketFieldId;
  new_value: JSON;
  old_value: JSON;
}

export interface TicketChangeHistory {
  ticket_change_history_id: string;
  ticket_id: string;
  fields: TicketChangeHistoryField[];
  system: boolean;
  ts: moment.Moment;
  author_id: string;
  author?: User;
  author_full_name?: string;
}

export class TicketChangeHistoryImpl implements TicketChangeHistory {
  @Type(() => TicketChangeHistoryFieldImpl)
  fields: TicketChangeHistoryField[];
  system: boolean;
  ticket_change_history_id: string;
  ticket_id: string;
  @Type(() => moment)
  @Type(() => moment)
  @Transform(({value}) => value ? moment.utc(value) : value, { toClassOnly: true })
  @Transform(({value}) => value ? value.toJSON() : value, { toPlainOnly: true })
  ts: moment.Moment;
  author_id: string;
  @Type(() => UserImpl)
  author?: User;
  author_full_name?: string;
}


export class TicketLinkImpl implements TicketLink {
  ticket_link_type: TicketLinkType;
  direction: TicketLinkDirection;

  @Type(() => BaseTicketImpl, {
    discriminator: {
      property: 'type',
      subTypes: [
        { value: AlertTicketImpl, name: TicketType.ALERT },
        { value: EscalatedTicketImpl, name: TicketType.ESCALATED },
      ],
    },
    keepDiscriminatorProperty: true
  })
  ticket: AlertTicketImpl | EscalatedTicketImpl;
}

export enum RuleState {
  RUNNING = 'running',
  PAUSED = 'paused',
  DELETED = 'deleted'
}

export interface TicketAggregationSearchCriteria {
  type?: TicketType;
  site_ids?: number[];
  status_ids?: TicketStatusId[];
  ticket_archived?: boolean;
  created_at_local_start?: string;
  created_at_local_end?: string;
  updated_at_local_start?: string;
  updated_at_local_end?: string;
  resolved_at_local_start?: string;
  resolved_at_local_end?: string;
  in_alert_local_ts_ranges?: string[];
  linked_ticket_recorded_at_local_end?: string;
  age_cutoff_local_ts?: string;
  resolved?: boolean;
  rule_state?: RuleState;
  has_links?: boolean;
  time_in_fault_boundaries?: number[];
  age_boundaries?: number[];
}

export interface TicketAggregationParams extends TicketAggregationSearchCriteria {
  aggregation?: TicketAggregation[];
  age?: boolean;
  time_in_fault?: boolean;
}

export interface BulkTicketAggregationParams {
  aggregation?: TicketAggregation[];
  references?: boolean;
  time_in_fault?: boolean;
  age?: boolean;
  bulk_search_criteria: TicketAggregationSearchCriteria[];
}


export interface TicketReferenceData {
  site?: SiteInfo;
  assigneesById?: Map<string, User>;
  prioritiesById?: Map<PriorityId, Priority>;
}


interface TicketAPIResult {
  affected_tickets?: Partial<TicketBody>[];
}

export interface TicketAPIResultFull<T extends TicketBody> extends TicketAPIResult {
  ticket: T;
}

export interface TicketAPIResultPartial<T extends TicketBody> extends TicketAPIResult {
  ticket: Partial<T>;
}


export interface TicketCommentResult {
  ticket?: PartialTicket,
  ticket_comment: TicketComment;
}

interface TicketCommentAPIResponseData {
  ticket?: Partial<TicketBody>;
  ticket_comment: TicketComment;
}

export type TicketCommentAPIResponse = APIResponse<TicketCommentAPIResponseData>;

export interface CommonAlertCacheData {
  metadataTypesByIdCache: CacheById<MetadataTypeId, MetadataType>;
  metadataByIdCache: CacheById<MetadataId, Metadata>;
  templatesBySiteIdCache: CacheById<SiteId, Template[]>;
  alertNotificationsByIdCache: CacheById<SiteNotificationId, AlertNotification>;
  siteNotificationIdsBySiteIdCache: CacheById<SiteId, SiteNotificationId[]>;
  equipmentIdsBySiteIdCache: CacheById<SiteId, EquipmentId[]>;
  equipmentCache: CacheById<EquipmentId, Equipment>;
}

export interface AlertDataNavigationState {
  cacheImportData?: CommonAlertCacheData;
}
